import { keyBy, snakeCase } from 'lodash/fp';
import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AccountStatusSticker from 'dashboard/components/AccountStatusSticker';
import ClickableDetail from 'dashboard/components/ClickableDetail';
import FieldAttributeItem from 'dashboard/components/FieldAttributeItem';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import {
  formatAddressFieldDisplay,
  formatNormalizedPostalCode,
} from 'dashboard/lib/utils/formatters';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { calculateHideOrShowAttribute } from 'dashboard/modules/Resource/calculateHideOrShow';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import { Field, FieldsMap, FieldString, parseField } from 'lib/fields';

const SPECIAL_LEGACY_ACCOUNT_TYPE_FIELDS = [
  'name',
  'address',
  'identification_numbers',
  'selfie_photo',
];

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AccountTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LinkRow = styled('div')`
  grid-column-end: span 3;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
`;

interface ResourceAccountProps {
  account: Account;
  accountType?: AccountType;
  accountStatus?: AccountStatus;
  titleFieldKey?: string;
  hideEmptyAttributes?: boolean;
  defaultAccordionOpen?: boolean;
}

const ResourceAccount = ({
  account,
  accountType,
  accountStatus,
  titleFieldKey,
  hideEmptyAttributes = false,
  defaultAccordionOpen = true,
}: ResourceAccountProps) => {
  const isRedacted = account.attributes['redacted-at'] != null;

  const isLegacyAccountType = accountType?.attributes['is-legacy-account-type'];

  const fieldSchemaMap = useMemo(
    () => keyBy(({ key }) => key, accountType?.attributes['field-schemas']),
    [accountType],
  );

  if (accountType == null) {
    return (
      <ClickableDetail
        header={
          <HeaderContainer>
            <Header>
              <AccountTitleContainer>
                <Heading size='sm'>Account – {account.id}</Heading>
              </AccountTitleContainer>
            </Header>
            <LinkRow>
              <StyledLink to={`/dashboard/accounts/${account.id}`}>{account.id}</StyledLink>
            </LinkRow>
          </HeaderContainer>
        }
        isDefaultOpen={defaultAccordionOpen}
      >
        <Card>
          <Card.Body>
            <Paragraph>No account type found for this account.</Paragraph>
          </Card.Body>
        </Card>
      </ClickableDetail>
    );
  }

  const fields = account.attributes.fields ?? {};
  const fieldNamesToShow = Object.keys(fields)
    .filter((fieldName) => {
      return fieldSchemaMap[snakeCase(fieldName)] != null;
    })
    .filter((fieldName) => {
      // For legacy accounts, we render the special legacy fields separately and so we want
      // to filter them out of the general fields section here.
      const isSpecialLegacyField = SPECIAL_LEGACY_ACCOUNT_TYPE_FIELDS.includes(fieldName);

      return isLegacyAccountType ? !isSpecialLegacyField : true;
    });

  const fieldsAttributes: ReactNode[] = fieldNamesToShow.map((fieldName: string) => {
    const fieldValue: Field = fields[fieldName];
    const fieldSchema = fieldSchemaMap[snakeCase(fieldName)];

    return (
      <FieldAttributeItem
        key={fieldSchema.key}
        field={fieldValue}
        fieldSchema={fieldSchema}
        isRedacted={isRedacted}
        hideEmptyAttributes={hideEmptyAttributes}
        wordBreak='break-all'
      />
    );
  });

  let title: ReactNode;
  let fallbackTitle: ReactNode;
  if (titleFieldKey) {
    title = String(account.attributes.fields[titleFieldKey]?.value ?? '');
    fallbackTitle = `No value collected for ${titleFieldKey}`;
  } else {
    title = account.attributes['name-full-localized'] ?? account.attributes['name-full'];
    fallbackTitle = 'No name collected';
  }

  return (
    <ClickableDetail
      header={
        <HeaderContainer>
          <Header>
            <AccountTitleContainer>
              {title ? (
                <Heading size='sm'>{title}</Heading>
              ) : (
                <Heading size='sm' color='tertiary'>
                  {fallbackTitle}
                </Heading>
              )}
              <Spacer width={1} />
              {accountStatus && <AccountStatusSticker accountStatus={accountStatus} />}
            </AccountTitleContainer>
          </Header>
          <LinkRow>
            <StyledLink to={`/dashboard/accounts/${account.id}`}>{account.id}</StyledLink>
          </LinkRow>
        </HeaderContainer>
      }
      isDefaultOpen={defaultAccordionOpen}
    >
      <Card>
        <Card.Body>
          <AttributeGrid>
            {isLegacyAccountType &&
              renderLegacyFieldAttributes(account, fields, isRedacted, (value, attribute) =>
                calculateHideOrShowAttribute(hideEmptyAttributes, value, attribute),
              )}
            {fieldsAttributes}
          </AttributeGrid>
        </Card.Body>
      </Card>
    </ClickableDetail>
  );
};

const renderLegacyFieldAttributes = (
  account: Account,
  fields: FieldsMap,
  isRedacted: boolean,
  calculateHideOrShowAttribute: (value: any, attribute: JSX.Element) => JSX.Element | null,
) => {
  const fieldAddress = account.attributes.fields['address']?.value as Record<string, FieldString>;

  const addressStreet1 = fieldAddress?.street_1?.value;
  const addressStreet2 = fieldAddress?.street_2?.value;
  const addressCity = fieldAddress?.city?.value;
  const addressSubdivision = fieldAddress?.subdivision?.value;
  const addressPostalCode = fieldAddress?.postal_code?.value;
  const addressCountryCode = fieldAddress?.country_code?.value;

  const { 'name-full': nameFull, 'name-full-localized': nameFullLocalized } = account.attributes;

  const nameDisplay = nameFullLocalized ?? nameFull;

  const normalizedPostalCode = formatNormalizedPostalCode({
    addressPostalCode: addressPostalCode,
    addressCountryCode: addressCountryCode,
  });

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: normalizedPostalCode,
  });

  const selfieFieldValue = parseField(fields['selfie_photo']);

  return (
    <>
      {calculateHideOrShowAttribute(
        selfieFieldValue,
        <Attribute
          label='Selfie photo'
          value={
            <AsyncImage
              imageUrl={isRedacted ? null : (selfieFieldValue as string)}
              modalLabel='Selfie photo'
              allowModalExpand={true}
              width='100%'
            />
          }
          redacted={isRedacted}
          key='selfie-photo'
          placeholder='--'
          wordBreak='break-word'
        />,
      )}
      {calculateHideOrShowAttribute(
        nameDisplay,
        <Attribute
          label='Full name'
          value={nameDisplay}
          redacted={isRedacted}
          key='name-full'
          placeholder='--'
          wordBreak='break-word'
        />,
      )}
      {calculateHideOrShowAttribute(
        address,
        <Attribute
          label='Address'
          value={address}
          redacted={isRedacted}
          key='address'
          placeholder='--'
          wordBreak='break-word'
        />,
      )}
    </>
  );
};

export default ResourceAccount;
